import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CheckSquare, FileText, Users, Mail, Plus, Archive, X, Circle, CheckCircle2, Trash2, Send, Pencil, Download, Paperclip, Video, Calendar } from "lucide-react";
import { useGoogleIntegration } from "@/hooks/useGoogleIntegration";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import jsPDF from 'jspdf';
import { TaskAssigneeSelector } from "@/components/TaskAssigneeSelector";

interface Meeting {
  id: string;
  title: string;
  meeting_date: string;
  location: string | null;
  meeting_type: string | null;
  custom_meeting_type: string | null;
  topics: Topic[];
  invited_members: string[];
  status: string;
  archived_at: string | null;
  sections?: MeetingSection[];
  google_calendar_event_id?: string | null;
  google_meet_url?: string | null;
}

interface Topic {
  title: string;
  details: string;
}

interface MeetingSection {
  id: string;
  title: string;
  content: string | null;
  section_order: number;
}

interface TopicAction {
  description: string;
  assignedTo: string[];
}

interface TopicMinutes {
  topicTitle: string;
  minutes: string;
  actions: TopicAction[];
}


interface Member {
  id: string;
  full_name: string;
  email: string;
  user_id: string | null;
  user_roles: { role: string }[];
}

interface Task {
  id: string;
  title: string;
  description: string | null;
  task_type: 'personal' | 'assigned' | 'general';
  assigned_to: string | null;
  status: 'pending' | 'completed';
  created_at: string;
  created_by: string | null;
  assigned_members?: Member[];
  role_context?: string | null;
  task_group_id?: string | null;
  assigned_role?: string | null;
}

function ArchivedMeetingsTable({ onViewMinutes, onRestore, ptaId }: { onViewMinutes: (meeting: string) => void; onRestore: (meeting: string) => void; ptaId: string | null }) {
  const [archivedMeetings, setArchivedMeetings] = useState<Meeting[]>([]);

  useEffect(() => {
    if (ptaId) {
      fetchArchivedMeetings();
    }
  }, [ptaId]);

  const fetchArchivedMeetings = async () => {
    if (!ptaId) return;
    
    const { data, error } = await (supabase as any)
      .from('meetings')
      .select('*')
      .eq('pta_id', ptaId)
      .not('archived_at', 'is', null)
      .order('meeting_date', { ascending: false });
    
    if (!error && data) setArchivedMeetings(data);
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Title</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Date</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {archivedMeetings.map((meeting) => (
          <TableRow key={meeting.id}>
            <TableCell className="font-medium">{meeting.title}</TableCell>
            <TableCell>{meeting.meeting_type}</TableCell>
            <TableCell>{new Date(meeting.meeting_date).toLocaleDateString()}</TableCell>
            <TableCell>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => onViewMinutes(`${meeting.title}`)}>
                  <FileText className="h-4 w-4 mr-1" />
                  Minutes
                </Button>
                <Button variant="outline" size="sm" onClick={() => onRestore(meeting.title)}>Restore</Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
        {archivedMeetings.length === 0 && (
          <TableRow>
            <TableCell colSpan={4} className="text-center text-muted-foreground">
              No archived meetings
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

export default function SecretaryDashboard() {
  const { toast } = useToast();
  const [isMeetingDialogOpen, setIsMeetingDialogOpen] = useState(false);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [ptaId, setPtaId] = useState<string | null>(null);
  
  // Meeting form states
  const [meetingType, setMeetingType] = useState<string>('Committee');
  const [customMeetingType, setCustomMeetingType] = useState<string>('');
  const [topics, setTopics] = useState<Topic[]>([{ title: "", details: "" }]);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [commRecipients, setCommRecipients] = useState<string[]>([]);
  const [commSubject, setCommSubject] = useState('');
  const [commMessage, setCommMessage] = useState('');
  const [commAttachment, setCommAttachment] = useState<File | null>(null);
  const [commMeetingAttachment, setCommMeetingAttachment] = useState<string>('');
  
  // Task form states
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [taskAssignees, setTaskAssignees] = useState<string[]>([]);
  const [taskRoles, setTaskRoles] = useState<string[]>([]);
  const [isAddTaskDialogOpen, setIsAddTaskDialogOpen] = useState(false);
  
  // Edit task states
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [editTaskTitle, setEditTaskTitle] = useState('');
  const [editTaskDescription, setEditTaskDescription] = useState('');
  
  // Edit topics states
  const [editingMeetingTopics, setEditingMeetingTopics] = useState<Meeting | null>(null);
  const [editTopics, setEditTopics] = useState<Topic[]>([]);
  
  // Minutes states
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [topicMinutes, setTopicMinutes] = useState<TopicMinutes[]>([]);
  const [attendees, setAttendees] = useState<string[]>([]);
  const [apologies, setApologies] = useState<string[]>([]);
  const [includeGoogleMeet, setIncludeGoogleMeet] = useState(false);
  const [syncToGoogleCalendar, setSyncToGoogleCalendar] = useState(false);

  const { isConnected: googleConnected, isCalendarEnabled, isMeetEnabled, syncToCalendar } = useGoogleIntegration(ptaId);

  useEffect(() => {
    fetchCurrentUser();
    fetchPtaId();
  }, []);

  useEffect(() => {
    if (ptaId) {
      fetchMeetings();
      fetchMembers();
      fetchTasks();
    }
  }, [ptaId, currentUser]);

  const fetchPtaId = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user.id)
      .single();
    
    if (!error && data) setPtaId(data.pta_id);
  };

  const fetchCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setCurrentUser(user);
  };

  const fetchMeetings = async () => {
    if (!ptaId) return;
    const { data, error } = await (supabase as any)
      .from('meetings')
      .select('*')
      .eq('pta_id', ptaId)
      .is('archived_at', null)
      .order('meeting_date', { ascending: false });
    
    if (!error && data) {
      const updatedMeetings = data.map((meeting: Meeting) => {
        const meetingDate = new Date(meeting.meeting_date);
        const now = new Date();
        if (meetingDate < now && meeting.status === 'upcoming') {
          supabase.from('meetings').update({ status: 'taken-place' }).eq('id', meeting.id);
          return { ...meeting, status: 'taken-place' };
        }
        return meeting;
      });
      setMeetings(updatedMeetings);
    }
  };

  const fetchMembers = async () => {
    if (!ptaId) return;
    const { data, error } = await supabase
      .from('members')
      .select('id, full_name, email, user_id, role')
      .eq('pta_id', ptaId)
      .eq('status', 'active')
      .order('full_name');
    
    if (!error && data) setMembers(data.map(m => ({ ...m, user_roles: [] })) as any);
  };

  const fetchTasks = async () => {
    if (!currentUser || !ptaId) return;
    
    // Fetch tasks: 
    // 1. Tasks created by me from this dashboard (role_context = 'secretary')
    // 2. Tasks assigned TO me (from any dashboard)
    const { data, error } = await (supabase as any)
      .from('tasks')
      .select('*')
      .eq('pta_id', ptaId)
      .or(`and(role_context.eq.secretary,created_by.eq.${currentUser.id}),assigned_to.eq.${currentUser.id}`)
      .order('created_at', { ascending: false });
    
    if (!error && data) setTasks(data);
  };

  const handleCreateMeeting = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    
    const meetingTitle = formData.get('title') as string;
    const meetingDate = formData.get('meeting_date') as string;
    const meetingLocation = formData.get('location') as string;
    
    const { data: newMeeting, error } = await (supabase as any).from('meetings').insert({
      title: meetingTitle,
      meeting_date: meetingDate,
      location: meetingLocation,
      meeting_type: meetingType === 'Other' ? null : meetingType,
      custom_meeting_type: meetingType === 'Other' ? customMeetingType : null,
      topics: topics as any,
      invited_members: selectedMembers,
      status: 'upcoming',
      created_by: currentUser?.id,
      pta_id: ptaId,
    }).select('id').single();

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    // Sync to Google Calendar and/or get Meet link
    let googleMeetUrl = '';
    if ((syncToGoogleCalendar || includeGoogleMeet) && newMeeting) {
      const syncResult = await syncToCalendar({
        action: 'create',
        eventType: 'meeting',
        eventId: newMeeting.id,
        title: meetingTitle,
        startDateTime: meetingDate,
        location: meetingLocation,
        includeGoogleMeet: includeGoogleMeet,
      });
      if (syncResult.success && syncResult.googleMeetUrl) {
        googleMeetUrl = syncResult.googleMeetUrl;
      }
    }

    // Send email notifications to invited members
    if (selectedMembers.length > 0 && ptaId) {
      try {
        const { data: memberEmails } = await supabase
          .from('members')
          .select('email')
          .in('id', selectedMembers)
          .eq('pta_id', ptaId);

        const { data: ptaData } = await supabase
          .from('ptas')
          .select('name')
          .eq('id', ptaId)
          .single();

        const ptaName = ptaData?.name || 'Your PTA';
        const displayMeetingType = meetingType === 'Other' ? customMeetingType : meetingType;
        const formattedDate = new Date(meetingDate).toLocaleString('en-GB', {
          weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
          hour: '2-digit', minute: '2-digit',
        });

        const topicsList = topics
          .filter(t => t.title)
          .map(t => `<div style="padding:8px 12px;margin-bottom:4px;background:#f8fafc;border-radius:4px;font-size:14px;color:#1a1a2e;"><strong>${t.title}</strong>${t.details ? `<br/><span style="color:#64748b;">${t.details}</span>` : ''}</div>`)
          .join('') || '<p style="color:#64748b;font-size:14px;">No agenda items listed.</p>';

        const googleMeetSection = googleMeetUrl
          ? `<div style="margin-bottom:20px;padding:16px;background:#e8f5e9;border-radius:8px;text-align:center;"><p style="margin:0 0 8px;color:#2e7d32;font-weight:600;">Google Meet Link</p><a href="${googleMeetUrl}" style="display:inline-block;padding:10px 24px;background:#1a73e8;color:#ffffff;text-decoration:none;border-radius:6px;font-weight:600;">Join Google Meet</a></div>`
          : '';

        const emails = memberEmails?.map(m => m.email).filter(Boolean) || [];
        if (emails.length > 0) {
          await supabase.functions.invoke('send-email', {
            body: {
              templateName: 'meeting_notification',
              recipients: emails,
              dynamicData: {
                meeting_title: meetingTitle,
                meeting_date: formattedDate,
                meeting_location: meetingLocation || 'TBC',
                meeting_type: displayMeetingType || 'Meeting',
                topics_list: topicsList,
                google_meet_section: googleMeetSection,
                pta_name: ptaName,
              },
            },
          });
        }
      } catch (emailError) {
        console.error('Error sending meeting notifications:', emailError);
      }
    }

    setIsMeetingDialogOpen(false);
    setTopics([{ title: "", details: "" }]);
    setSelectedMembers([]);
    setMeetingType('Committee');
    setCustomMeetingType('');
    setIncludeGoogleMeet(false);
    setSyncToGoogleCalendar(false);
    fetchMeetings();
    
    toast({
      title: "Meeting created",
      description: "Meeting has been scheduled and members notified.",
    });
  };

  const handleArchiveMeeting = async (meetingId: string) => {
    const meeting = meetings.find(m => m.id === meetingId);
    
    // Check if meeting has minutes
    const { data: sections } = await supabase
      .from('meeting_sections')
      .select('*')
      .eq('meeting_id', meetingId);
    
    if (!sections || sections.length === 0) {
      const confirmed = window.confirm(
        "This meeting has no minutes recorded. Are you sure you want to archive it?"
      );
      if (!confirmed) return;
    }

    const { error } = await supabase
      .from('meetings')
      .update({ archived_at: new Date().toISOString() })
      .eq('id', meetingId);

    if (!error) {
      fetchMeetings();
      toast({
        title: "Meeting archived",
        description: "Meeting has been moved to archive.",
      });
    }
  };

  const handleSaveMinutes = async () => {
    if (!selectedMeeting) {
      toast({
        title: "Error",
        description: "No meeting selected.",
        variant: "destructive",
      });
      return;
    }

    // Build minutes content with topics and attendance
    let content = "ATTENDANCE\n\n";
    content += "Present:\n";
    attendees.forEach(id => {
      const member = members.find(m => m.id === id);
      if (member) content += `- ${member.full_name}\n`;
    });
    content += "\nApologies:\n";
    apologies.forEach(id => {
      const member = members.find(m => m.id === id);
      if (member) content += `- ${member.full_name}\n`;
    });
    content += "\n\nMINUTES\n\n";
    
    topicMinutes.forEach((tm, idx) => {
      content += `${idx + 1}. ${tm.topicTitle}\n${tm.minutes}\n\n`;
    });

    const { error } = await supabase
      .from('meeting_sections')
      .insert({
        meeting_id: selectedMeeting.id,
        title: 'Meeting Minutes',
        content: content,
        section_order: 0,
        created_by: currentUser?.id,
        pta_id: ptaId,
      });

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Minutes saved",
      description: "Meeting minutes have been saved successfully.",
    });
    
    setTopicMinutes([]);
    setAttendees([]);
    setApologies([]);
    setSelectedMeeting(null);
  };

  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newTaskTitle) {
      toast({
        title: "Error",
        description: "Please enter a task title.",
        variant: "destructive",
      });
      return;
    }

    if (taskAssignees.length === 0 && taskRoles.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one role or person to assign the task to.",
        variant: "destructive",
      });
      return;
    }

    const tasksToInsert: any[] = [];
    const taskGroupId = crypto.randomUUID(); // Group ID for role-based tasks
    
    // If only "me" is selected and no roles, create personal task
    if (taskAssignees.length === 1 && taskAssignees.includes('me') && taskRoles.length === 0) {
      tasksToInsert.push({
        title: newTaskTitle,
        description: newTaskDescription || null,
        task_type: 'personal',
        assigned_to: currentUser?.id,
        status: 'pending',
        created_by: currentUser?.id,
        pta_id: ptaId,
        role_context: 'secretary',
      });
    } else {
      // Handle "me" separately if selected with others
      if (taskAssignees.includes('me')) {
        tasksToInsert.push({
          title: newTaskTitle,
          description: newTaskDescription || null,
          task_type: 'personal',
          assigned_to: currentUser?.id,
          status: 'pending',
          created_by: currentUser?.id,
          pta_id: ptaId,
          role_context: 'secretary',
        });
      }
      
      // For individual members, create assigned tasks (no group)
      const otherAssignees = taskAssignees.filter(id => id !== 'me');
      for (const assigneeId of otherAssignees) {
        tasksToInsert.push({
          title: newTaskTitle,
          description: newTaskDescription || null,
          task_type: 'assigned',
          assigned_to: assigneeId,
          status: 'pending',
          created_by: currentUser?.id,
          pta_id: ptaId,
          role_context: 'secretary',
        });
      }

      // For roles, find all members with those roles and create tasks for them (with group)
      if (taskRoles.length > 0) {
        const { data: roleMembers } = await supabase
          .from('user_roles')
          .select('user_id, role');
        
        if (roleMembers) {
          const assignedRolesString = taskRoles.join(',');
          for (const role of taskRoles) {
            const usersWithRole = roleMembers.filter(rm => rm.role === role);
            for (const ur of usersWithRole) {
              // Find the member ID for this user in this PTA
              const member = members.find(m => m.user_id === ur.user_id);
              if (member && member.user_id && !otherAssignees.includes(member.user_id)) {
                // Avoid duplicates if member was also selected individually
                tasksToInsert.push({
                  title: newTaskTitle,
                  description: newTaskDescription || null,
                  task_type: 'assigned',
                  assigned_to: member.user_id,
                  status: 'pending',
                  created_by: currentUser?.id,
                  pta_id: ptaId,
                  role_context: 'secretary',
                  task_group_id: taskGroupId,
                  assigned_role: assignedRolesString,
                });
              }
            }
          }
        }
      }
    }

    const { error } = await supabase.from('tasks').insert(tasksToInsert);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Task added",
      description: tasksToInsert.length === 1 && taskAssignees.includes('me') && taskRoles.length === 0
        ? "Personal task created." 
        : `Task(s) assigned to ${tasksToInsert.length} recipient(s).`,
    });
    
    setNewTaskTitle("");
    setNewTaskDescription("");
    setTaskAssignees([]);
    setTaskRoles([]);
    setIsAddTaskDialogOpen(false);
    fetchTasks();
  };

  const handleToggleTask = async (taskId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    const { error } = await supabase
      .from('tasks')
      .update({ 
        status: newStatus,
        completed_at: newStatus === 'completed' ? new Date().toISOString() : null 
      })
      .eq('id', taskId);

    if (!error) {
      fetchTasks();
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    const { error } = await supabase
      .from('tasks')
      .delete()
      .eq('id', taskId);

    if (!error) {
      fetchTasks();
    }
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setEditTaskTitle(task.title);
    setEditTaskDescription(task.description || '');
  };

  const handleSaveEditTask = async () => {
    if (!editingTask || !editTaskTitle.trim()) return;
    
    const { error } = await supabase
      .from('tasks')
      .update({
        title: editTaskTitle,
        description: editTaskDescription || null,
      })
      .eq('id', editingTask.id);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Task updated",
      description: "Task has been updated successfully.",
    });
    
    setEditingTask(null);
    setEditTaskTitle('');
    setEditTaskDescription('');
    fetchTasks();
  };

  const handleUpdateTopics = async () => {
    if (!editingMeetingTopics) return;
    
    const { error } = await supabase
      .from('meetings')
      .update({ topics: editTopics as any })
      .eq('id', editingMeetingTopics.id);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Topics updated",
      description: "Meeting topics have been updated successfully.",
    });
    
    setEditingMeetingTopics(null);
    setEditTopics([]);
    fetchMeetings();
  };

  const handleViewMinutes = (meeting: string) => {
    toast({
      title: "View minutes",
      description: `${meeting} minutes would open here.`,
    });
  };
  
  const handleRestoreMeeting = (meeting: string) => {
    toast({
      title: "Meeting restored",
      description: `${meeting} has been restored from archive.`,
    });
  };
  
  const handleSendEmail = (e: React.FormEvent) => {
    e.preventDefault();
    if (!commSubject || !commMessage || commRecipients.length === 0) {
      toast({
        title: "Error",
        description: "Please enter a subject, message and select at least one recipient.",
        variant: "destructive",
      });
      return;
    }
    toast({
      title: "Email sent",
      description: `Your message has been sent to ${commRecipients.length} recipient(s).`,
    });
    setCommSubject('');
    setCommMessage('');
    setCommRecipients([]);
    setCommAttachment(null);
    setCommMeetingAttachment('');
  };
  
  const generateAgendaPDF = (meeting: Meeting) => {
    const doc = new jsPDF();
    const meetingDate = new Date(meeting.meeting_date);
    const formattedDate = meetingDate.toLocaleDateString('en-GB');
    
    // Title
    doc.setFontSize(18);
    doc.text('Meeting Agenda', 105, 20, { align: 'center' });
    
    // Meeting details
    doc.setFontSize(12);
    doc.text(`Meeting: ${meeting.title}`, 20, 40);
    doc.text(`Type: ${meeting.meeting_type || meeting.custom_meeting_type || 'General'}`, 20, 50);
    doc.text(`Date: ${meetingDate.toLocaleString()}`, 20, 60);
    if (meeting.location) {
      doc.text(`Location: ${meeting.location}`, 20, 70);
    }
    
    // Topics
    let yPos = meeting.location ? 90 : 80;
    doc.setFontSize(14);
    doc.text('Agenda Items:', 20, yPos);
    
    doc.setFontSize(11);
    if (meeting.topics && meeting.topics.length > 0) {
      meeting.topics.forEach((topic, idx) => {
        yPos += 12;
        if (yPos > 270) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(`${idx + 1}. ${topic.title}`, 25, yPos);
        if (topic.details) {
          yPos += 8;
          const lines = doc.splitTextToSize(topic.details, 160);
          lines.forEach((line: string) => {
            if (yPos > 270) {
              doc.addPage();
              yPos = 20;
            }
            doc.text(line, 30, yPos);
            yPos += 6;
          });
        }
      });
    } else {
      yPos += 12;
      doc.text('No agenda items specified.', 25, yPos);
    }
    
    // Generate filename
    const filename = `${meeting.title.replace(/[^a-z0-9]/gi, '-')}-${formattedDate.replace(/\//g, '-')}.pdf`;
    doc.save(filename);
  };

  const personalTasks = tasks.filter(t => t.task_type === 'personal' || t.task_type === 'assigned');

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
        <h1 className="text-4xl font-bold text-foreground">
          Secretary Dashboard
        </h1>
          <p className="text-muted-foreground mt-2">Manage meetings, minutes, and member coordination</p>
        </div>
      </div>

      <Tabs defaultValue="tasks" className="w-full">
        <TabsList className="inline-flex h-auto items-center justify-start gap-8 border-b border-border w-full">
          <TabsTrigger value="tasks" className="inline-flex items-center justify-center whitespace-nowrap pb-3 px-0 text-sm font-medium text-muted-foreground transition-all border-b-2 border-transparent data-[state=active]:text-primary data-[state=active]:border-primary hover:text-foreground">Tasks</TabsTrigger>
          <TabsTrigger value="meetings" className="inline-flex items-center justify-center whitespace-nowrap pb-3 px-0 text-sm font-medium text-muted-foreground transition-all border-b-2 border-transparent data-[state=active]:text-primary data-[state=active]:border-primary hover:text-foreground">Meetings</TabsTrigger>
          <TabsTrigger value="communications" className="inline-flex items-center justify-center whitespace-nowrap pb-3 px-0 text-sm font-medium text-muted-foreground transition-all border-b-2 border-transparent data-[state=active]:text-primary data-[state=active]:border-primary hover:text-foreground">Communications</TabsTrigger>
        </TabsList>

        {/* Tasks Tab */}
        <TabsContent value="tasks" className="space-y-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Tasks</CardTitle>
                <CardDescription>Manage personal and assigned tasks</CardDescription>
              </div>
              <Dialog open={isAddTaskDialogOpen} onOpenChange={setIsAddTaskDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Task
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Task</DialogTitle>
                    <DialogDescription>Create a new task and assign it to members</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleAddTask} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="new-task-title">Task Title</Label>
                      <Input
                        id="new-task-title"
                        placeholder="Task title..."
                        value={newTaskTitle}
                        onChange={(e) => setNewTaskTitle(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="new-task-description">Description (Optional)</Label>
                      <Textarea
                        id="new-task-description"
                        placeholder="Description..."
                        value={newTaskDescription}
                        onChange={(e) => setNewTaskDescription(e.target.value)}
                        rows={3}
                      />
                    </div>
                    <TaskAssigneeSelector
                      ptaId={ptaId}
                      members={members}
                      selectedAssignees={taskAssignees}
                      selectedRoles={taskRoles}
                      onAssigneesChange={setTaskAssignees}
                      onRolesChange={setTaskRoles}
                      includeMe={true}
                    />
                    <div className="flex justify-end">
                      <Button type="submit">Add Task</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* My Tasks - includes personal tasks and tasks assigned TO me by others */}
              <div>
                <h3 className="font-medium mb-2">My Tasks</h3>
                <div className="space-y-2">
                  {personalTasks.filter(t => t.task_type === 'personal' || (t.task_type === 'assigned' && t.assigned_to === currentUser?.id && t.created_by !== currentUser?.id)).length === 0 ? (
                    <p className="text-sm text-muted-foreground">No tasks</p>
                  ) : (
                    personalTasks.filter(t => t.task_type === 'personal' || (t.task_type === 'assigned' && t.assigned_to === currentUser?.id && t.created_by !== currentUser?.id)).map((task) => (
                      <div key={task.id} className="flex items-center gap-2 p-3 border rounded-lg">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleTask(task.id, task.status)}
                        >
                          {task.status === 'completed' ? (
                            <CheckCircle2 className="h-5 w-5 text-green-600" />
                          ) : (
                            <Circle className="h-5 w-5" />
                          )}
                        </Button>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className={`font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                              {task.title}
                            </p>
                            {task.created_by !== currentUser?.id && task.role_context && (
                              <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                From: {task.role_context.charAt(0).toUpperCase() + task.role_context.slice(1)}
                              </span>
                            )}
                          </div>
                          {task.description && (
                            <p className="text-sm text-muted-foreground">{task.description}</p>
                          )}
                        </div>
                        {task.created_by === currentUser?.id && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              title="Edit task"
                              onClick={() => handleEditTask(task)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              title="Delete task"
                              onClick={() => handleDeleteTask(task.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Assigned Tasks - tasks I created and assigned to others */}
              <div>
                <h3 className="font-medium mb-2">Assigned Tasks</h3>
                <div className="space-y-2">
                  {(() => {
                    const myAssignedTasks = personalTasks.filter(t => t.task_type === 'assigned' && t.created_by === currentUser?.id);
                    if (myAssignedTasks.length === 0) {
                      return <p className="text-sm text-muted-foreground">No assigned tasks</p>;
                    }
                    
                    // Group tasks by task_group_id, non-grouped tasks get their own entry
                    const groupedTasks: Record<string, Task[]> = {};
                    const individualTasks: Task[] = [];
                    
                    myAssignedTasks.forEach(task => {
                      if (task.task_group_id) {
                        if (!groupedTasks[task.task_group_id]) {
                          groupedTasks[task.task_group_id] = [];
                        }
                        groupedTasks[task.task_group_id].push(task);
                      } else {
                        individualTasks.push(task);
                      }
                    });
                    
                    const ROLE_NAMES: Record<string, string> = {
                      chair: 'Chair', secretary: 'Secretary', treasurer: 'Treasurer',
                      trustee: 'Trustee', events: 'Events', community: 'Community',
                      catering: 'Catering', assets: 'Assets', shop: 'Shop', school_liaison: 'School Liaison'
                    };
                    
                    return (
                      <>
                        {/* Grouped (role-based) tasks */}
                        {Object.entries(groupedTasks).map(([groupId, tasks]) => {
                          const firstTask = tasks[0];
                          const allCompleted = tasks.every(t => t.status === 'completed');
                          const completedCount = tasks.filter(t => t.status === 'completed').length;
                          const roleNames = firstTask.assigned_role?.split(',').map(r => ROLE_NAMES[r] || r).join(', ');
                          
                          return (
                            <div key={groupId} className="p-3 border rounded-lg">
                              <div className="flex items-start gap-2">
                                <div className={`h-5 w-5 flex items-center justify-center mt-0.5 ${allCompleted ? 'text-green-600' : 'text-muted-foreground'}`}>
                                  {allCompleted ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-5 w-5" />}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <p className={`font-medium ${allCompleted ? 'line-through text-muted-foreground' : ''}`}>
                                      {firstTask.title}
                                    </p>
                                    <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                      {roleNames}
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                      ({completedCount}/{tasks.length} completed)
                                    </span>
                                  </div>
                                  {firstTask.description && (
                                    <p className="text-sm text-muted-foreground mt-1">{firstTask.description}</p>
                                  )}
                                  <div className="mt-2 flex flex-wrap gap-1">
                                    {tasks.map(task => {
                                      const member = members.find(m => m.user_id === task.assigned_to);
                                      return (
                                        <span 
                                          key={task.id} 
                                          className={`text-xs px-2 py-0.5 rounded ${task.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-secondary text-secondary-foreground'}`}
                                        >
                                          {member?.full_name || 'Unknown'} {task.status === 'completed' && '✓'}
                                        </span>
                                      );
                                    })}
                                  </div>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  title="Delete all tasks in group"
                                  onClick={async () => {
                                    for (const task of tasks) {
                                      await handleDeleteTask(task.id);
                                    }
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                        
                        {/* Individual tasks */}
                        {individualTasks.map((task) => {
                          const assignedMember = members.find(m => m.user_id === task.assigned_to);
                          return (
                            <div key={task.id} className="flex items-center gap-2 p-3 border rounded-lg">
                              <div className={`h-5 w-5 flex items-center justify-center ${task.status === 'completed' ? 'text-green-600' : 'text-muted-foreground'}`}>
                                {task.status === 'completed' ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-5 w-5" />}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className={`font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                                    {task.title}
                                  </p>
                                  {task.status === 'completed' && (
                                    <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                      Completed
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  Assigned to: {assignedMember?.full_name || 'Unknown'}
                                </p>
                                {task.description && (
                                  <p className="text-sm text-muted-foreground">{task.description}</p>
                                )}
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                title="Edit task"
                                onClick={() => handleEditTask(task)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                title="Delete task"
                                onClick={() => handleDeleteTask(task.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          );
                        })}
                      </>
                    );
                  })()}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Edit Task Dialog */}
          <Dialog open={!!editingTask} onOpenChange={(open) => {
            if (!open) {
              setEditingTask(null);
              setEditTaskTitle("");
              setEditTaskDescription("");
            }
          }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Task</DialogTitle>
                <DialogDescription>Update task details</DialogDescription>
              </DialogHeader>
              <form onSubmit={(e) => { e.preventDefault(); handleSaveEditTask(); }} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-task-title">Task Title</Label>
                  <Input
                    id="edit-task-title"
                    value={editTaskTitle}
                    onChange={(e) => setEditTaskTitle(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-task-description">Description (Optional)</Label>
                  <Textarea
                    id="edit-task-description"
                    value={editTaskDescription}
                    onChange={(e) => setEditTaskDescription(e.target.value)}
                    rows={3}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setEditingTask(null)}>
                    Cancel
                  </Button>
                  <Button type="submit">Save Changes</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Meetings Tab */}
        <TabsContent value="meetings" className="space-y-3">
          <Card className="shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Meetings & Minutes</CardTitle>
                  <CardDescription>Manage meetings and record minutes</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline">
                        <Archive className="h-4 w-4 mr-2" />
                        Archive
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Archived Meetings</DialogTitle>
                        <DialogDescription>View and retrieve past meeting records</DialogDescription>
                      </DialogHeader>
                      <ArchivedMeetingsTable onViewMinutes={handleViewMinutes} onRestore={handleRestoreMeeting} ptaId={ptaId} />
                    </DialogContent>
                  </Dialog>
                  <Dialog open={isMeetingDialogOpen} onOpenChange={setIsMeetingDialogOpen}>
                    <DialogTrigger asChild>
                      <Button>
                        <Plus className="h-4 w-4 mr-2" />
                        New Meeting
                      </Button>
                    </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Create New Meeting</DialogTitle>
                      <DialogDescription>Schedule a committee meeting</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateMeeting} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="meeting_title">Meeting Title</Label>
                        <Input id="meeting_title" name="title" required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="meeting_type">Meeting Type</Label>
                        <Select value={meetingType} onValueChange={setMeetingType} required>
                          <SelectTrigger>
                            <SelectValue placeholder="Select meeting type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="AGM">AGM</SelectItem>
                            <SelectItem value="EGM">EGM</SelectItem>
                            <SelectItem value="Committee">Committee</SelectItem>
                            <SelectItem value="Event Planning">Event Planning</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {meetingType === 'Other' && (
                        <div className="space-y-2">
                          <Label htmlFor="custom_type">Custom Meeting Type</Label>
                          <Input
                            id="custom_type"
                            value={customMeetingType}
                            onChange={(e) => setCustomMeetingType(e.target.value)}
                            placeholder="Enter custom meeting type"
                            required
                          />
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label htmlFor="meeting_date">Meeting Date & Time</Label>
                        <Input id="meeting_date" name="meeting_date" type="datetime-local" required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="location">Location</Label>
                        <Input id="location" name="location" />
                      </div>
                      <div className="space-y-2">
                        <Label>Topics</Label>
                        {topics.map((topic, index) => (
                          <div key={index} className="space-y-2 p-3 border rounded">
                            <Input
                              placeholder="Topic title"
                              value={topic.title}
                              onChange={(e) => {
                                const newTopics = [...topics];
                                newTopics[index].title = e.target.value;
                                setTopics(newTopics);
                              }}
                            />
                            <Textarea
                              placeholder="Topic details"
                              value={topic.details}
                              onChange={(e) => {
                                const newTopics = [...topics];
                                newTopics[index].details = e.target.value;
                                setTopics(newTopics);
                              }}
                              rows={2}
                            />
                            {topics.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const newTopics = topics.filter((_, i) => i !== index);
                                  setTopics(newTopics);
                                }}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setTopics([...topics, { title: "", details: "" }])}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add Topic
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <Label>Invite Members</Label>
                        <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border rounded p-2">
                          {members.map((member) => (
                            <div key={member.id} className="flex items-center space-x-2">
                              <Checkbox
                                id={`member-${member.id}`}
                                checked={selectedMembers.includes(member.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedMembers([...selectedMembers, member.id]);
                                  } else {
                                    setSelectedMembers(selectedMembers.filter(id => id !== member.id));
                                  }
                                }}
                              />
                              <Label htmlFor={`member-${member.id}`} className="text-sm cursor-pointer">
                                {member.full_name}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>
                      {googleConnected && (isCalendarEnabled || isMeetEnabled) && (
                        <div className="p-3 border rounded bg-muted/50 space-y-2">
                          {isCalendarEnabled && (
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="sync-calendar-sec"
                                checked={syncToGoogleCalendar}
                                onCheckedChange={(checked) => setSyncToGoogleCalendar(!!checked)}
                              />
                              <Label htmlFor="sync-calendar-sec" className="text-sm cursor-pointer flex items-center gap-2">
                                <Calendar className="h-4 w-4" />
                                Sync to Google Calendar
                              </Label>
                            </div>
                          )}
                          {isMeetEnabled && (
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="include-meet-sec"
                                checked={includeGoogleMeet}
                                onCheckedChange={(checked) => setIncludeGoogleMeet(!!checked)}
                              />
                              <Label htmlFor="include-meet-sec" className="text-sm cursor-pointer flex items-center gap-2">
                                <Video className="h-4 w-4" />
                                Include Google Meet link
                              </Label>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="flex justify-end gap-2">
                        <DialogClose asChild>
                          <Button type="button" variant="outline">Cancel</Button>
                        </DialogClose>
                        <Button type="submit">Create Meeting</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {meetings.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No meetings scheduled</p>
                ) : (
                  meetings.map((meeting) => (
                    <div key={meeting.id} className="p-4 border rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold">{meeting.title}</h3>
                          <p className="text-sm text-muted-foreground">
                            {meeting.meeting_type || meeting.custom_meeting_type}
                          </p>
                          <p className="text-sm">
                            {new Date(meeting.meeting_date).toLocaleString()}
                          </p>
                          {meeting.location && (
                            <p className="text-sm text-muted-foreground">{meeting.location}</p>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            Status: {meeting.status}
                          </p>
                          {meeting.google_meet_url && (
                            <a
                              href={meeting.google_meet_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1"
                            >
                              <Video className="h-3 w-3" />
                              Join Google Meet
                            </a>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 w-8 p-0"
                                  onClick={() => {
                                    setSelectedMeeting(meeting);
                                    if (meeting.topics && Array.isArray(meeting.topics)) {
                                      setTopicMinutes(meeting.topics.map((t: Topic) => ({ 
                                        topicTitle: t.title, 
                                        minutes: '',
                                        actions: []
                                      })));
                                    }
                                  }}
                                >
                                  <FileText className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Minutes</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 w-8 p-0"
                                  onClick={() => {
                                    setEditingMeetingTopics(meeting);
                                    setEditTopics(meeting.topics?.length > 0 ? [...meeting.topics] : [{ title: "", details: "" }]);
                                  }}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Edit Topics</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => generateAgendaPDF(meeting)}
                            className="h-8 w-8 p-0"
                            title="Download agenda PDF"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          {meeting.status === 'taken-place' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleArchiveMeeting(meeting.id)}
                            >
                              Archive
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {/* Edit Topics Dialog */}
              <Dialog open={editingMeetingTopics !== null} onOpenChange={(open) => {
                if (!open) {
                  setEditingMeetingTopics(null);
                  setEditTopics([]);
                }
              }}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Edit Topics - {editingMeetingTopics?.title}</DialogTitle>
                    <DialogDescription>Update the agenda topics for this meeting</DialogDescription>
                  </DialogHeader>
                  {editingMeetingTopics && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label>Topics</Label>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setEditTopics([...editTopics, { title: "", details: "" }])}
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Add Topic
                          </Button>
                        </div>
                        {editTopics.map((topic, index) => (
                          <div key={index} className="space-y-2 p-3 border rounded">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{index + 1}.</span>
                              <Input
                                placeholder="Topic title"
                                value={topic.title}
                                onChange={(e) => {
                                  const newTopics = [...editTopics];
                                  newTopics[index].title = e.target.value;
                                  setEditTopics(newTopics);
                                }}
                                className="flex-1"
                              />
                              {editTopics.length > 1 && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={() => {
                                          setEditTopics(editTopics.filter((_, i) => i !== index));
                                        }}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Delete topic</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                            <Textarea
                              placeholder="Topic details"
                              value={topic.details}
                              onChange={(e) => {
                                const newTopics = [...editTopics];
                                newTopics[index].details = e.target.value;
                                setEditTopics(newTopics);
                              }}
                              rows={2}
                            />
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-end gap-2 border-t pt-4">
                        <Button variant="outline" onClick={() => {
                          setEditingMeetingTopics(null);
                          setEditTopics([]);
                        }}>
                          Cancel
                        </Button>
                        <Button onClick={handleUpdateTopics}>
                          Save Topics
                        </Button>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
              
              {/* Minutes Dialog */}
              <Dialog open={selectedMeeting !== null} onOpenChange={(open) => {
                if (!open) {
                  setSelectedMeeting(null);
                  setTopicMinutes([]);
                  setAttendees([]);
                  setApologies([]);
                }
              }}>
                <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{selectedMeeting?.title} - Minutes</DialogTitle>
                    <DialogDescription>Record meeting attendance and minutes for each topic</DialogDescription>
                  </DialogHeader>
                  {selectedMeeting && (
                    <div className="space-y-6">
                      <div>
                        <Label className="text-muted-foreground">Date & Time</Label>
                        <p>{new Date(selectedMeeting.meeting_date).toLocaleString()}</p>
                      </div>
                      
                      {/* Attendees */}
                      <div className="space-y-3 border-t pt-4">
                        <h3 className="font-semibold">Attendees</h3>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <Label className="mb-2 block">Attendees</Label>
                            <div className="border rounded p-2 max-h-40 overflow-y-auto space-y-1">
                              {members.map((member) => (
                                <div key={member.id} className={`flex items-center space-x-2 ${apologies.includes(member.id) ? 'opacity-50' : ''}`}>
                                  <Checkbox
                                    id={`attend-${member.id}`}
                                    checked={attendees.includes(member.id)}
                                    disabled={apologies.includes(member.id)}
                                    onCheckedChange={(checked) => {
                                      if (checked) {
                                        setAttendees([...attendees, member.id]);
                                      } else {
                                        setAttendees(attendees.filter(id => id !== member.id));
                                      }
                                    }}
                                  />
                                  <Label htmlFor={`attend-${member.id}`} className="text-sm cursor-pointer">
                                    {member.full_name}
                                  </Label>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div>
                            <Label className="mb-2 block">Apologies</Label>
                            <div className="border rounded p-2 max-h-40 overflow-y-auto space-y-1">
                              {members.map((member) => (
                                <div key={member.id} className={`flex items-center space-x-2 ${attendees.includes(member.id) ? 'opacity-50' : ''}`}>
                                  <Checkbox
                                    id={`apology-${member.id}`}
                                    checked={apologies.includes(member.id)}
                                    disabled={attendees.includes(member.id)}
                                    onCheckedChange={(checked) => {
                                      if (checked) {
                                        setApologies([...apologies, member.id]);
                                      } else {
                                        setApologies(apologies.filter(id => id !== member.id));
                                      }
                                    }}
                                  />
                                  <Label htmlFor={`apology-${member.id}`} className="text-sm cursor-pointer">
                                    {member.full_name}
                                  </Label>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Topics and Minutes */}
                      <div className="space-y-3 border-t pt-4">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold">Topic Minutes</h3>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setTopicMinutes([...topicMinutes, { topicTitle: `Topic ${topicMinutes.length + 1}`, minutes: '', actions: [] }]);
                            }}
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Add Topic
                          </Button>
                        </div>
                        {topicMinutes.map((tm, index) => (
                          <div key={index} className="space-y-3 p-3 border rounded">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{index + 1}.</span>
                              <Input
                                value={tm.topicTitle}
                                onChange={(e) => {
                                  const updated = [...topicMinutes];
                                  updated[index].topicTitle = e.target.value;
                                  setTopicMinutes(updated);
                                }}
                                className="flex-1 font-medium"
                              />
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0"
                                      onClick={() => {
                                        setTopicMinutes(topicMinutes.filter((_, i) => i !== index));
                                      }}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Delete topic</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                            <Textarea
                              placeholder="Enter minutes for this topic..."
                              value={tm.minutes}
                              onChange={(e) => {
                                const updated = [...topicMinutes];
                                updated[index].minutes = e.target.value;
                                setTopicMinutes(updated);
                              }}
                              rows={3}
                            />
                            
                            {/* Actions Section */}
                            <div className="space-y-2 pt-2 border-t">
                              <div className="flex items-center justify-between">
                                <Label className="text-sm font-medium">Actions</Label>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const updated = [...topicMinutes];
                                    updated[index].actions = [...(updated[index].actions || []), { description: '', assignedTo: [] }];
                                    setTopicMinutes(updated);
                                  }}
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  Add Action
                                </Button>
                              </div>
                              {(tm.actions || []).map((action, actionIndex) => (
                                <div key={actionIndex} className="space-y-2 p-2 bg-muted/50 rounded">
                                  <div className="flex gap-2">
                                    <Input
                                      placeholder="Action description..."
                                      value={action.description}
                                      onChange={(e) => {
                                        const updated = [...topicMinutes];
                                        updated[index].actions[actionIndex].description = e.target.value;
                                        setTopicMinutes(updated);
                                      }}
                                      className="flex-1"
                                    />
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        const updated = [...topicMinutes];
                                        updated[index].actions = updated[index].actions.filter((_, i) => i !== actionIndex);
                                        setTopicMinutes(updated);
                                      }}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs text-muted-foreground">Assign to:</Label>
                                    <div className="grid grid-cols-2 gap-1 max-h-24 overflow-y-auto">
                                      {members.map((member) => (
                                        <div key={member.id} className="flex items-center space-x-1">
                                          <Checkbox
                                            id={`action-${index}-${actionIndex}-${member.id}`}
                                            checked={action.assignedTo.includes(member.id)}
                                            onCheckedChange={(checked) => {
                                              const updated = [...topicMinutes];
                                              if (checked) {
                                                updated[index].actions[actionIndex].assignedTo = [...updated[index].actions[actionIndex].assignedTo, member.id];
                                              } else {
                                                updated[index].actions[actionIndex].assignedTo = updated[index].actions[actionIndex].assignedTo.filter(id => id !== member.id);
                                              }
                                              setTopicMinutes(updated);
                                            }}
                                          />
                                          <Label htmlFor={`action-${index}-${actionIndex}-${member.id}`} className="text-xs cursor-pointer">
                                            {member.full_name}
                                          </Label>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                        {topicMinutes.length === 0 && (
                          <p className="text-sm text-muted-foreground">No topics for this meeting</p>
                        )}
                      </div>

                      <div className="flex justify-end gap-2 border-t pt-4">
                        <Button variant="outline" onClick={() => {
                          setSelectedMeeting(null);
                          setTopicMinutes([]);
                          setAttendees([]);
                          setApologies([]);
                        }}>
                          Cancel
                        </Button>
                        <Button onClick={handleSaveMinutes}>
                          Save Minutes
                        </Button>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="communications" className="space-y-3">
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Email Members</CardTitle>
              <CardDescription>Send communications to PTA members</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSendEmail} className="space-y-4">
                <div className="space-y-2">
                  <Label>Recipients</Label>
                  <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border rounded p-2">
                    {members.map((member) => (
                      <div key={member.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`email-member-${member.id}`}
                          checked={commRecipients.includes(member.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setCommRecipients([...commRecipients, member.id]);
                            } else {
                              setCommRecipients(commRecipients.filter(id => id !== member.id));
                            }
                          }}
                        />
                        <Label htmlFor={`email-member-${member.id}`} className="text-sm cursor-pointer">
                          {member.full_name}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email-subject">Subject</Label>
                  <Input 
                    id="email-subject" 
                    placeholder="Email subject" 
                    value={commSubject}
                    onChange={(e) => setCommSubject(e.target.value)}
                    required 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email-message">Message</Label>
                  <Textarea 
                    id="email-message" 
                    placeholder="Type your message here..." 
                    value={commMessage}
                    onChange={(e) => setCommMessage(e.target.value)}
                    rows={8} 
                    required 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Attachments</Label>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Input
                        type="file"
                        id="sec-file-attachment"
                        className="flex-1"
                        onChange={(e) => {
                          setCommAttachment(e.target.files?.[0] || null);
                          setCommMeetingAttachment("");
                        }}
                      />
                      {commAttachment && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setCommAttachment(null)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Or attach meeting agenda:</span>
                      <Select 
                        value={commMeetingAttachment} 
                        onValueChange={(value) => {
                          setCommMeetingAttachment(value === "none" ? "" : value);
                          setCommAttachment(null);
                        }}
                      >
                        <SelectTrigger className="w-[200px]">
                          <SelectValue placeholder="Select meeting" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {meetings.map((meeting) => (
                            <SelectItem key={meeting.id} value={meeting.id}>
                              {meeting.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {(commAttachment || commMeetingAttachment) && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Paperclip className="h-3 w-3" />
                        {commAttachment ? commAttachment.name : `Agenda: ${meetings.find(m => m.id === commMeetingAttachment)?.title}`}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button type="submit">
                    <Send className="h-4 w-4 mr-2" />
                    Send Email
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
